import type { SupabaseClient } from '../../lib/supabase';
import type { Campaign, ContentItem } from './types';

/**
 * Database access for the Nurture Studio. Row Level Security scopes every
 * read (any active TAP person) and every write (Admins and Members); the
 * approval boundary lives in a database guard, so nothing here is trusted.
 */

// One literal, deliberately unwrapped (PostgREST type inference).
// prettier-ignore
const CONTENT_COLUMNS = 'id, title, campaign_id, bucket, status, status_changed_at, scheduled_on, drafter_id, subject, body, notes, review_notes, approved_by, approved_at, times_used, last_used_at, created_by, created_at, updated_by, updated_at';

// prettier-ignore
const CAMPAIGN_COLUMNS = 'id, name, type, audience, starts_on, ends_on, owner_id, status, notes, created_by, created_at, updated_at';

function fail(message: string): never {
  throw new Error(message);
}

export async function fetchCampaigns(client: SupabaseClient): Promise<Campaign[]> {
  const { data, error } = await client
    .from('campaigns')
    .select(CAMPAIGN_COLUMNS)
    .order('created_at', { ascending: false });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function fetchContentItems(client: SupabaseClient): Promise<ContentItem[]> {
  const { data, error } = await client
    .from('content_items')
    .select(CONTENT_COLUMNS)
    .order('created_at', { ascending: false });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export interface ContentInput {
  title: string;
  campaignId: string | null;
  bucket: string;
  scheduledOn: string | null;
  subject: string;
  body: string;
  notes: string;
}

export async function createContentItem(
  client: SupabaseClient,
  input: ContentInput & { createdBy: string },
): Promise<ContentItem> {
  const { data, error } = await client
    .from('content_items')
    .insert({
      title: input.title.trim(),
      campaign_id: input.campaignId,
      bucket: input.bucket.trim(),
      scheduled_on: input.scheduledOn,
      subject: input.subject,
      body: input.body,
      notes: input.notes,
      created_by: input.createdBy,
    })
    .select(CONTENT_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  return data;
}

export type ContentUpdate = Partial<{
  title: string;
  campaign_id: string | null;
  bucket: string;
  status: ContentItem['status'];
  scheduled_on: string | null;
  drafter_id: string | null;
  subject: string;
  body: string;
  notes: string;
  review_notes: string;
  updated_by: string;
}>;

export async function updateContentItem(
  client: SupabaseClient,
  itemId: string,
  changes: ContentUpdate,
): Promise<ContentItem> {
  const { data, error } = await client
    .from('content_items')
    .update(changes)
    .eq('id', itemId)
    .select(CONTENT_COLUMNS)
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('content_not_permitted');
  }
  return data;
}

export async function deleteContentItem(client: SupabaseClient, itemId: string): Promise<void> {
  const { error } = await client.from('content_items').delete().eq('id', itemId);
  if (error) {
    fail(error.message);
  }
}

export interface CampaignInput {
  name: string;
  type: string;
  audience: string;
  startsOn: string | null;
  endsOn: string | null;
  notes: string;
}

export async function createCampaign(
  client: SupabaseClient,
  input: CampaignInput & { createdBy: string },
): Promise<Campaign> {
  const { data, error } = await client
    .from('campaigns')
    .insert({
      name: input.name.trim(),
      type: input.type.trim(),
      audience: input.audience.trim(),
      starts_on: input.startsOn,
      ends_on: input.endsOn,
      notes: input.notes,
      created_by: input.createdBy,
      owner_id: input.createdBy,
    })
    .select(CAMPAIGN_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  return data;
}

export async function updateCampaign(
  client: SupabaseClient,
  campaignId: string,
  changes: Partial<
    Pick<Campaign, 'name' | 'type' | 'audience' | 'starts_on' | 'ends_on' | 'status' | 'notes'>
  >,
): Promise<Campaign> {
  const { data, error } = await client
    .from('campaigns')
    .update(changes)
    .eq('id', campaignId)
    .select(CAMPAIGN_COLUMNS)
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('content_not_permitted');
  }
  return data;
}

export async function deleteCampaign(client: SupabaseClient, campaignId: string): Promise<void> {
  const { error } = await client.from('campaigns').delete().eq('id', campaignId);
  if (error) {
    fail(error.message);
  }
}
